import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {Warehouse, Search, Plus, Edit, Trash2, Package, AlertTriangle, AlertCircle, CheckCircle, Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, Eye, X, XCircle, LayoutGrid, Shield, Layers, Gauge, FileText, Clock, Check, PauseCircle, MapPin, Box, Tag} from 'lucide-react';
import InventaireListPageShell from './components/InventaireListPageShell';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import { InventaireModalPortal, InventaireViewModalPortal } from './components/inventaireModalPortal';
import FacturationFormModalTabs from '../facturation/components/FacturationFormModalTabs';
import {
  sliceQawaemStats,
  buildInventaireImportExportProps,
  buildInventaireColumnVisibilityState,
  buildInventaireQawaemColumnDefs,
  isInventaireColumnVisible,
  inventaireTableColSpan,
  nextInventaireEntityRef,
  InventoryConfigNotice,
  useInventaireListSettings,
  sliceInventaireListItems,
  inventaireRecordInDateRange,
  INVENTAIRE_FORM_GRID,
  buildInventaireBulkDeleteFilterAction,
  InventaireTableSelectAllHeader,
  InventaireTableRowCheckbox,
} from './inventaireListPageUtils';
import { useInventoryConfig } from './useInventoryConfig';
import { useSettings } from '../../core/contexts/SettingsContext';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import fr from 'date-fns/locale/fr';
import arLocale from 'date-fns/locale/ar-SA';
import { getBrandDefaultCountry, getBrandCountryOptions } from '../../shared/utils/brandCountry';
import { getBrandLocale, getBrandDatePickerLocale } from '../../shared/utils/brandCurrency';
import 'react-datepicker/dist/react-datepicker.css';
import '../../shared/styles/GPSTrackingDatePicker.css';
import '../../shared/components/custom/CustomComponents.css';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { useTranslation } from 'react-i18next';
import FacturationViewModeToggle from '../facturation/components/FacturationViewModeToggle';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, exactColumnRule, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomNumberInput from '../../shared/components/custom/CustomNumberInput';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';

registerLocale('fr', fr);
registerLocale('ar', arLocale);
registerLocale('ar-SA', arLocale);

const WarehousesPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.inventaire.pages.warehousesPage.${key}`, opts);
  const { isDashboardLightMode } = useGlobalTheme();
  const dateLocale = getBrandLocale(i18n.language);
  const datePickerLocale = getBrandDatePickerLocale();
  const defaultCountry = getBrandDefaultCountry();
  const { settings } = useSettings();
  const inventoryConfig = useInventoryConfig();
  const { dateRange, viewMode, setViewMode, itemsPerPage } = useInventaireListSettings(inventoryConfig);

  const getStatusLabel = (status) => {
    const map = {
      active: p('status_active'),
      inactive: p('status_inactive'),
      maintenance: p('status_maintenance'),
      construction: p('status_construction')
    };
    return map[status] || p('status_active');
  };

  const getTypeLabel = (type) => {
    const map = {
      main: p('type_main'),
      secondary: p('type_secondary'),
      distribution: p('type_distribution'),
      transit: p('type_transit')
    };
    return map[type] || p('type_standard');
  };

  const getExportHeaders = () => [
    { key: 'name', label: p('col_name') },
    { key: 'code', label: p('col_code') },
    { key: 'address', label: p('col_address') },
    { key: 'city', label: p('col_city') },
    { key: 'postal_code', label: p('col_postal_code') },
    { key: 'country', label: p('col_country') },
    { key: 'capacity', label: p('col_capacity_sqm') },
    { key: 'current_stock', label: p('col_current_stock') },
    { key: 'status', label: p('col_status') },
    { key: 'type', label: p('type_label') },
    { key: 'contact_person', label: p('col_contact') },
    { key: 'phone', label: p('col_phone') },
    { key: 'email', label: p('col_email') },
    { key: 'manager', label: p('col_manager') }
  ];
  // Close dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Status dropdown
      const isClickOnStatusButton = event.target.closest('[data-warehouse-status-dropdown-button]');
      const isClickInStatusDropdown = event.target.closest('[data-warehouse-status-dropdown]');
      if (!isClickOnStatusButton && !isClickInStatusDropdown) {
        setShowStatusDropdown(false);
      }
      
      // Type dropdown
      const isClickOnTypeButton = event.target.closest('[data-warehouse-type-dropdown-button]');
      const isClickInTypeDropdown = event.target.closest('[data-warehouse-type-dropdown]');
      if (!isClickOnTypeButton && !isClickInTypeDropdown) {
        setShowTypeDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const [searchTerm, setSearchTerm] = useState('');
  const [warehouses, setWarehouses] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  
  const [warehouseFormTab, setWarehouseFormTab] = useState('warehouseInfo');
  
  const [formData, setFormData] = useState({ 
    name: '', 
    code: '', 
    address: '', 
    city: '', 
    postal_code: '', 
    country: defaultCountry,
    latitude: '', 
    longitude: '',
    status: 'active', 
    type: 'main',
    capacity: '',
    current_stock: '',
    contact_person: '',
    phone: '',
    email: '',
    manager: '',
    notes: ''
  });

  const countryOptions = useMemo(() => {
    const base = getBrandCountryOptions();
    const seen = new Set(base.map((opt) => opt.value));
    const extras = warehouses
      .map((row) => (row.country || '').trim())
      .filter((name) => name && !seen.has(name));
    const uniqueExtras = [...new Set(extras)];
    if (formData.country?.trim() && !seen.has(formData.country.trim()) && !uniqueExtras.includes(formData.country.trim())) {
      uniqueExtras.push(formData.country.trim());
    }
    return [
      ...base,
      ...uniqueExtras.map((name) => ({ value: name, label: name })),
    ];
  }, [warehouses, formData.country]);
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_warehouses');
  const [quickFilter, setQuickFilter] = useState('all');
  // PDF and Excel export states
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);
  const typeDropdownRef = useRef(null);

  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  
  const warehouseFormTabs = useMemo(() => ([
    { id: 'warehouseInfo', label: p('section_warehouse_info'), icon: Warehouse, iconColor: 'text-emerald-400' },
    { id: 'contactSecurity', label: p('section_contact_security'), icon: Shield, iconColor: 'text-red-400' },
  ]), [p]);

  useEffect(() => { loadWarehouses(); }, []);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) setShowStatusDropdown(false);
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(event.target)) setShowTypeDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadWarehouses = async () => {
    try {
      const response = await localApiAdapter.get('/inventory/warehouses');
      setWarehouses(sortRecordsNewestFirst(response?.data || response || []));
    } catch (error) {
      console.error('Error loading warehouses:', error);
      setWarehouses([]);
    }
  };

  const stats = {
    total: warehouses.length,
    active: warehouses.filter(w => w.status === 'active').length,
    inactive: warehouses.filter(w => w.status === 'inactive').length,
    maintenance: warehouses.filter(w => w.status === 'maintenance').length,
    capacity: warehouses.reduce((sum, w) => sum + (parseFloat(w.capacity) || 0), 0)
  };

  const mainTabs = [
    { id: 'all_warehouses', name: p('tab_general'), icon: LayoutGrid },
    { id: 'par_statut', name: p('tab_by_status'), icon: Shield },
    { id: 'par_capacite', name: p('tab_by_capacity'), icon: Gauge }
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('filter_all'), icon: Layers },
    { id: 'active', name: p('filter_active'), icon: CheckCircle },
    { id: 'inactive', name: p('filter_inactive'), icon: XCircle },
    { id: 'maintenance', name: p('filter_maintenance'), icon: AlertCircle }
  ];

  const warehouseDistribution = {
    all_warehouses: {
      title: p('overview_title'),
      data: [
        { id: 'active', name: p('dist_active'), count: stats.active, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { id: 'inactive', name: p('dist_inactive'), count: stats.inactive, color: "bg-gradient-to-r from-red-500 to-red-600" },
        { id: 'maintenance', name: p('dist_maintenance'), count: stats.maintenance, color: "bg-gradient-to-r from-yellow-500 to-yellow-600" }
      ]
    },
    par_statut: {
      title: p('distribution_by_status'),
      data: [
        { id: 'active', name: p('dist_active'), count: stats.active, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { id: 'non_active', name: p('dist_non_active'), count: stats.inactive + stats.maintenance, color: "bg-gradient-to-r from-red-500 to-red-600" }
      ]
    },
    par_capacite: {
      title: p('distribution_by_capacity'),
      data: [
        { id: 'large', name: p('dist_large'), count: warehouses.filter(w => parseFloat(w.capacity) > 10000).length, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { id: 'medium', name: p('dist_medium'), count: warehouses.filter(w => parseFloat(w.capacity) >= 5000 && parseFloat(w.capacity) <= 10000).length, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { id: 'small', name: p('dist_small'), count: warehouses.filter(w => parseFloat(w.capacity) < 5000).length, color: "bg-gradient-to-r from-green-500 to-green-600" }
      ]
    }
  };

  const distributionInfo = warehouseDistribution[mainViewMode] || warehouseDistribution.all_warehouses;

  const baseFilteredWarehouses = warehouses.filter(w => {
    if (!inventaireRecordInDateRange(dateRange, w.created_at)) return false;

    // Search filter
    if (searchTerm && !w.name?.toLowerCase().includes(searchTerm.toLowerCase()) && 
        !w.code?.toLowerCase().includes(searchTerm.toLowerCase()) && 
        !w.city?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    
    // Quick filter (only for Vue Générale)
    if (mainViewMode === 'all_warehouses') {
      if (quickFilter === 'active' && w.status !== 'active') return false;
      if (quickFilter === 'inactive' && w.status !== 'inactive') return false;
      if (quickFilter === 'maintenance' && w.status !== 'maintenance') return false;
    }
    
    // Distribution card filter (clickable stats)
    if (activeDistributionFilter) {
      switch(mainViewMode) {
        case 'all_warehouses':
          if (activeDistributionFilter === 'active' && w.status !== 'active') return false;
          if (activeDistributionFilter === 'inactive' && w.status !== 'inactive') return false;
          if (activeDistributionFilter === 'maintenance' && w.status !== 'maintenance') return false;
          break;
        case 'par_statut':
          if (activeDistributionFilter === 'active' && w.status !== 'active') return false;
          if (activeDistributionFilter === 'non_active' && (w.status !== 'inactive' && w.status !== 'maintenance')) return false;
          break;
        case 'par_capacite':
          const capacity = parseFloat(w.capacity) || 0;
          if (activeDistributionFilter === 'large' && capacity <= 10000) return false;
          if (activeDistributionFilter === 'medium' && (capacity < 5000 || capacity > 10000)) return false;
          if (activeDistributionFilter === 'small' && capacity >= 5000) return false;
          break;
      }
    }
    
    return true;
  });

  const columnFilterRules = useMemo(() => [
    textColumnRule('name'),
    textColumnRule('code'),
    textColumnRule('city'),
    textColumnRule('capacity', (r) => String(r.capacity ?? '')),
    textColumnRule('current_stock', (r) => String(r.current_stock ?? '')),
    textColumnRule('contact_person', (r) => String(r.contact_person ?? r.phone ?? r.email ?? '')),
    exactColumnRule('status', (r) => r.status),
  ], []);

  const filteredWarehouses = useMemo(
    () => applyColumnFilters(baseFilteredWarehouses, colFilters, columnFilterRules),
    [baseFilteredWarehouses, colFilters, columnFilterRules],
  );
  const visibleWarehouses = useMemo(
    () => sliceInventaireListItems(filteredWarehouses, itemsPerPage),
    [filteredWarehouses, itemsPerPage],
  );
  const generateWarehouseCode = useCallback(
    () => nextInventaireEntityRef(settings, 'warehouse', 'WH', warehouses, (w) => w.code),
    [settings, warehouses],
  );

  const openNewWarehouseModal = () => {
    if (!inventoryConfig.enableWarehouses) {
      window.alert(p('notice_warehouses_disabled', { defaultValue: 'إدارة المستودعات معطّلة من إعدادات المخزون.' }));
      return;
    }
    setEditingRecord(null);
    setWarehouseFormTab('warehouseInfo');
    setFormData({
      name: '',
      code: inventoryConfig.autoGenerateWarehouseReference !== false ? generateWarehouseCode() : '',
      address: '',
      city: '',
      postal_code: '',
      country: defaultCountry,
      latitude: '',
      longitude: '',
      status: 'active',
      type: 'main',
      capacity: '',
      current_stock: '',
      contact_person: '',
      phone: '',
      email: '',
      manager: '',
      notes: '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const warehouseCode = (formData.code || '').trim() || generateWarehouseCode();

      const submitData = { 
        name: formData.name,
        code: warehouseCode,
        address: formData.address,
        city: formData.city,
        postal_code: formData.postal_code,
        country: formData.country || defaultCountry,
        latitude: parseFloat(formData.latitude) || null,
        longitude: parseFloat(formData.longitude) || null,
        status: formData.status,
        type: formData.type,
        capacity: parseFloat(formData.capacity) || 0,
        current_stock: parseFloat(formData.current_stock) || 0,
        contact_person: formData.contact_person || '',
        phone: formData.phone || '',
        email: formData.email || '',
        manager: formData.manager || '',
        notes: formData.notes || ''
      };
      if (editingRecord) {
        await localApiAdapter.put(`/inventory/warehouses/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'المستودع' }));
      } else {
        await localApiAdapter.post('/inventory/warehouses', submitData);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'المستودع' }));
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('alert_save_error', { message: error.message || p('alert_unknown_error') }));
      return;
    } finally {
      resetForm();
      loadWarehouses();
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({ 
      name: record.name || '', 
      code: record.code || '', 
      address: record.address || '', 
      city: record.city || '', 
      postal_code: record.postal_code || '', 
      country: record.country || defaultCountry,
      latitude: record.latitude?.toString() || '', 
      longitude: record.longitude?.toString() || '',
      status: record.status || 'active',
      type: record.type || 'main',
      capacity: record.capacity?.toString() || '',
      current_stock: record.current_stock?.toString() || '',
      contact_person: record.contact_person || '',
      phone: record.phone || '',
      email: record.email || '',
      manager: record.manager || '',
      notes: record.notes || ''
    });
    setWarehouseFormTab('warehouseInfo');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/inventory/warehouses/${id}`);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'المستودع' }));
        loadWarehouses();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('alert_save_error', { message: error.message || p('alert_unknown_error') }));
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('delete_bulk_confirm', { count: selectedRecords.length }))) {
      try {
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/inventory/warehouses/${id}`)));
        notifyBulkDeleted(showNotify, selectedRecords.length, p('entity_label', { defaultValue: 'المستودع' }));
        setSelectedRecords([]);
        loadWarehouses();
      } catch (error) {
        console.error('Error deleting records:', error);
        notifyError(showNotify, p('alert_save_error', { message: error.message || p('alert_unknown_error') }));
      }
    }
  };

  const resetForm = () => {
    setFormData({ 
      name: '', 
      code: '', 
      address: '', 
      city: '', 
      postal_code: '', 
      country: defaultCountry,
      latitude: '', 
      longitude: '',
      status: 'active', 
      type: 'main',
      capacity: '',
      current_stock: '',
      contact_person: '',
      phone: '',
      email: '',
      manager: '',
      notes: ''
    });
    setEditingRecord(null);
    setWarehouseFormTab('warehouseInfo');
    setShowModal(false);
  };

  // ============================================
  // 📤 EXPORT FUNCTIONALITY
  // ============================================

  const handleExportCSV = () => {
    const headers = getExportHeaders();
    const dataToExport = filteredWarehouses;
    const fileName = `warehouses_export_${new Date().toISOString().split('T')[0]}.csv`;
    try {
      exportToCSV(dataToExport, headers, fileName);
    } catch (error) {
      console.error('Export error:', error);
      alert(p('alert_export_error'));
    }
  };

  // Export PDF
  const handleExportPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      
      const filteredData = filteredWarehouses;
      
      // Create a temporary table element for PDF generation
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = `
        <div class="report-content">
          <table>
            <thead>
              <tr>
                <th>${p('pdf_col_name')}</th>
                <th>${p('col_reference')}</th>
                <th>${p('col_city')}</th>
                <th>${p('col_capacity')}</th>
                <th>${p('pdf_col_stock')}</th>
                <th>${p('col_status')}</th>
              </tr>
            </thead>
            <tbody>
              ${filteredData.map(item => `
                <tr>
                  <td>${item.name || 'N/A'}</td>
                  <td>${item.code || 'N/A'}</td>
                  <td>${item.city || 'N/A'}</td>
                  <td>${item.capacity || '0'} m²</td>
                  <td>${item.current_stock || '0'}</td>
                  <td>${item.status || 'N/A'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `;
      
      document.body.appendChild(tempDiv);
      
      await generateReportPDF(p('report_title'), {});
      
      document.body.removeChild(tempDiv);
      
    } catch (error) {
      console.error('PDF generation error:', error);
      alert(p('alert_pdf_error'));
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Export Excel (Advanced)
  const handleExportExcel = async () => {
    try {
      setIsGeneratingExcel(true);
      
      const filteredData = filteredWarehouses;
      
      // Create a temporary table element for Excel generation
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = `
        <div class="report-content">
          <table>
            <thead>
              <tr>
                <th>${p('col_name')}</th>
                <th>${p('col_reference')}</th>
                <th>${p('col_address')}</th>
                <th>${p('col_city')}</th>
                <th>${p('col_postal_code')}</th>
                <th>${p('col_country')}</th>
                <th>${p('col_capacity_sqm')}</th>
                <th>${p('col_current_stock')}</th>
                <th>${p('col_status')}</th>
                <th>${p('type_label')}</th>
                <th>${p('col_contact')}</th>
                <th>${p('col_phone')}</th>
                <th>${p('col_email')}</th>
                <th>${p('col_manager')}</th>
              </tr>
            </thead>
            <tbody>
              ${filteredData.map(item => `
                <tr>
                  <td>${item.name || ''}</td>
                  <td>${item.code || ''}</td>
                  <td>${item.address || ''}</td>
                  <td>${item.city || ''}</td>
                  <td>${item.postal_code || ''}</td>
                  <td>${item.country || ''}</td>
                  <td>${item.capacity || '0'}</td>
                  <td>${item.current_stock || '0'}</td>
                  <td>${item.status || ''}</td>
                  <td>${item.type || ''}</td>
                  <td>${item.contact_person || ''}</td>
                  <td>${item.phone || ''}</td>
                  <td>${item.email || ''}</td>
                  <td>${item.manager || ''}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `;
      
      document.body.appendChild(tempDiv);
      
      await generateReportExcel('warehouses', p('report_title'), {});
      
      document.body.removeChild(tempDiv);
      
    } catch (error) {
      console.error('Excel generation error:', error);
      alert(p('alert_excel_error'));
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  // ============================================
  // 📥 IMPORT FUNCTIONALITY
  // ============================================

  const handleImport = () => {
    const headers = getExportHeaders();

    // Define validation schema
    const schema = {
      requiredFields: ['name', 'code'],
      validators: {
        capacity: (value) => !value || !isNaN(parseFloat(value)),
        current_stock: (value) => !value || !isNaN(parseFloat(value)),
        status: (value) => !value || ['active', 'inactive', 'maintenance'].includes(value)
      }
    };

    // Trigger file input
    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, headers, schema);
        
        if (result.success) {
          // Import valid records
          let successCount = 0;
          for (const record of result.data) {
            try {
              await localApiAdapter.post('/inventory/warehouses', record);
              successCount++;
            } catch (err) {
              console.error('Import record error:', err);
            }
          }
          
          // Reload data
          await loadWarehouses();
          
          // Show results
          const message = `${p('import_complete')}\n\n` +
            `${p('import_total', { count: result.totalCount })}\n` +
            `${p('import_success', { count: successCount })}\n` +
            (result.invalidCount > 0 ? `${p('import_errors', { count: result.invalidCount })}\n\n${result.errors.slice(0, 5).join('\n')}` : '');
          
          alert(message);
        }
      } catch (error) {
        console.error('Import error:', error);
        alert(p('alert_import_error'));
      }
    }, '.csv,.xlsx,.xls');
  };

  const filteredWarehouseIds = useMemo(() => filteredWarehouses.map((w) => w.id), [filteredWarehouses]);
  const allRowsSelected = filteredWarehouseIds.length > 0 && filteredWarehouseIds.every((id) => selectedRecords.includes(id));
  const someRowsSelected = selectedRecords.length > 0 && !allRowsSelected;

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredWarehouseIds);
    }
  };
  const handleSelectRecord = (id) => {
    setSelectedRecords((prev) => (prev.includes(id) ? prev.filter((rId) => rId !== id) : [...prev, id]));
  };

  const statusOptions = [
    { value: 'active', label: p('status_active'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'inactive', label: p('status_inactive'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
    { value: 'maintenance', label: p('status_maintenance'), icon: AlertCircle, style: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    { value: 'construction', label: p('status_construction'), icon: PauseCircle, style: 'bg-blue-500/20 text-blue-400 border-blue-500/30' }
  ];

  const handleStatusChange = async (recordId, newStatus) => {
    console.log('🔄 Changing status:', { recordId, newStatus });
    try {
      console.log('📡 Sending PUT request for warehouse:', recordId);
      await localApiAdapter.put(`/inventory/warehouses/${recordId}`, { status: newStatus });
      console.log('✅ Status updated successfully');
      await loadWarehouses();
    } catch (error) {
      console.error('❌ Error updating status:', error);
    }
  };


  const INVENTAIRE_TABLE_COLUMNS = [
    { key: 'name', labelKey: 'col_name' },
    { key: 'code', labelKey: 'col_code' },
    { key: 'city', labelKey: 'col_city' },
    { key: 'capacity', labelKey: 'col_capacity' },
    { key: 'current_stock', labelKey: 'col_current_stock' },
    { key: 'contact_person', labelKey: 'col_contact' },
    { key: 'status', labelKey: 'col_status' },
    { key: 'actions', labelKey: 'col_actions', required: true },
  ];

  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildInventaireColumnVisibilityState(INVENTAIRE_TABLE_COLUMNS),
  );
  const qawaemColumnDefs = useMemo(
    () => buildInventaireQawaemColumnDefs(INVENTAIRE_TABLE_COLUMNS, p, columnVisibility),
    [p, columnVisibility],
  );
  const handleToggleColumn = (key) => {
    setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => isInventaireColumnVisible(columnVisibility, key);
  const tableColSpan = inventaireTableColSpan(columnVisibility);

  const inventaireStatEntries = useMemo(() => sliceQawaemStats([
    { key: 'stat_total', label: p('stat_total'), value: stats.total, icon: Warehouse },
    { key: 'stat_active', label: p('stat_active'), value: stats.active, icon: CheckCircle },
    { key: 'stat_inactive', label: p('stat_inactive'), value: stats.inactive, icon: XCircle },
    { key: 'stat_maintenance', label: p('stat_maintenance'), value: stats.maintenance, icon: AlertCircle },
  ], 4), [stats, p]);

  const importExportProps = useMemo(() => buildInventaireImportExportProps({
    showImportDropdown,
    setShowImportDropdown,
    showExportDropdown,
    setShowExportDropdown,
    importDropdownRef,
    exportDropdownRef,
    importLabel: t('common.import'),
    exportLabel: t('common.export'),
    generatingLabel: p('generating'),
    isGeneratingPDF,
    handlers: {
      onImport: handleImport,
      onExportCSV: handleExportCSV,
      onExportPDF: handleExportPDF,
      onExportExcel: handleExportExcel,
    },
  }), [showImportDropdown, showExportDropdown, isGeneratingPDF, t, p]);

  return (
    <>
    <InventoryConfigNotice
      message={!inventoryConfig.enableWarehouses ? p('notice_warehouses_disabled_banner', { defaultValue: 'إدارة المستودعات معطّلة — فعّلها من إعدادات المخزون ← المستودعات.' }) : null}
    />
    <InventaireListPageShell
      title={p('title')}
      subtitle={p('subtitle')}
      icon={Warehouse}
      statEntries={inventaireStatEntries}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={p('search_placeholder')}
      filterActions={buildInventaireBulkDeleteFilterAction({ count: selectedRecords.length, onDelete: handleBulkDelete, p })}
      onCreate={openNewWarehouseModal}
      createLabel={p('new_warehouse')}
      importExport={importExportProps}
    >
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <InventaireTableSelectAllHeader allRowsSelected={allRowsSelected} someRowsSelected={someRowsSelected} onSelectAll={handleSelectAll} p={p} />
                  
                  {isColVisible('name') && (

                  
                    <TableColumnHeaderFilter columnKey="name" label={p('col_name')} value={colFilters.name ?? ''} onChange={(v) => setColFilter('name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('code') && (

                    <TableColumnHeaderFilter columnKey="code" label={p('col_code')} value={colFilters.code ?? ''} onChange={(v) => setColFilter('code', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('city') && (

                    <TableColumnHeaderFilter columnKey="city" label={p('col_city')} value={colFilters.city ?? ''} onChange={(v) => setColFilter('city', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('capacity') && (

                    <TableColumnHeaderFilter columnKey="capacity" label={p('col_capacity')} value={colFilters.capacity ?? ''} onChange={(v) => setColFilter('capacity', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('current_stock') && (

                    <TableColumnHeaderFilter columnKey="current_stock" label={p('col_current_stock')} value={colFilters.current_stock ?? ''} onChange={(v) => setColFilter('current_stock', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('contact_person') && (

                    <TableColumnHeaderFilter columnKey="contact_person" label={p('col_contact')} value={colFilters.contact_person ?? ''} onChange={(v) => setColFilter('contact_person', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('status') && (

                    <TableColumnHeaderFilter columnKey="status" label={p('col_status')} kind="select" options={statusOptions.map((s) => ({ value: s.value, label: s.label }))} value={colFilters.status ?? ''} onChange={(v) => setColFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white" />
                  )}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                    thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {visibleWarehouses.length > 0 ? (
                  visibleWarehouses.map((record, index) => {
                    // Helper function to get adaptive light text
                    const getAdaptiveLightText = () => {
                      const fillRate = record.capacity > 0 ? ((record.current_stock / record.capacity) * 100).toFixed(0) : 0;
                      return p('row_capacity_detail', {
                        capacity: record.capacity || 0,
                        rate: fillRate,
                        type: getTypeLabel(record.type)
                      });
                    };

                    return (
                      <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''} ${index === 0 ? 'first:rounded-t-3xl' : ''} ${index === visibleWarehouses.length - 1 ? 'last:rounded-b-3xl' : ''}`}>
                        <InventaireTableRowCheckbox checked={selectedRecords.includes(record.id)} onChange={() => handleSelectRecord(record.id)} />
                        
                        {/* Nom - with adaptive details */}
                        {isColVisible('name') && (

                          <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">{record.name}</span>
                            <span className="text-white/50 text-xs mt-0.5">{getAdaptiveLightText()}</span>
                          </div>
                        </td>

                        )}
                        
                        {/* Code - bold code, light type */}
                        {isColVisible('code') && (

                          <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">{record.code || '-'}</span>
                            <span className="text-white/50 text-xs mt-0.5">{p('type_label')}: {getTypeLabel(record.type)}</span>
                          </div>
                        </td>

                        )}
                        
                        {/* Ville - City bold, address light */}
                        {isColVisible('city') && (

                          <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <div className="flex items-center gap-1.5 text-white font-semibold">
                              <MapPin size={14} className="text-white/50" />
                              {record.city || '-'}
                            </div>
                            <span className="text-white/50 text-xs mt-0.5">
                              {record.address || p('address_not_provided')}
                            </span>
                          </div>
                        </td>

                        )}
                        
                        {/* Capacité - bold value, light percentage */}
                        {isColVisible('capacity') && (

                          <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">{record.capacity || 0} m²</span>
                            <span className="text-white/50 text-xs mt-0.5">
                              {record.capacity > 10000 ? p('capacity_large') : record.capacity > 5000 ? p('capacity_medium') : p('capacity_small')}
                            </span>
                          </div>
                        </td>

                        )}
                        
                        {/* Stock - bold value, light fill rate */}
                        {isColVisible('current_stock') && (

                          <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">{record.current_stock || 0}</span>
                            <span className="text-white/50 text-xs mt-0.5">
                              {p('fill_rate', { rate: record.capacity > 0 ? ((record.current_stock / record.capacity) * 100).toFixed(0) : 0 })}
                            </span>
                          </div>
                        </td>

                        )}
                        
                        {/* Contact - bold name, light phone */}
                        {isColVisible('contact_person') && (

                          <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">{record.contact_person || record.manager || '-'}</span>
                            <span className="text-white/50 text-xs mt-0.5">
                              {record.phone || p('not_provided')}
                            </span>
                          </div>
                        </td>

                        )}
                        
                        {isColVisible('status') && (

                        
                          <td className="px-6 py-4 text-center"><div className="flex justify-center"><QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status') || t('common.change_status')} /></div></td>

                        
                        )}
                        <td className="px-6 py-4">
                          <QawaemErpRowActions
                          onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                        />
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><Warehouse size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty')}</p></td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Card View - Expanded */}
        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {visibleWarehouses.length > 0 ? (
              visibleWarehouses.map((record, index) => (
                <motion.div
                  key={record.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <Warehouse size={20} className="text-emerald-400" />
                          {record.name}
                        </h3>
                        <div className="flex"><QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status') || t('common.change_status')} /></div>
                        {record.code && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-white/20 text-white/80">
                            {record.code}
                          </span>
                        )}
                      </div>
                      <div className={INVENTAIRE_FORM_GRID}>
                        {/* Informations Générales */}
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_general')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_reference')}:</span> {record.code || '-'}</div>
                            <div><span className="font-medium">{p('type_label')}:</span> {getTypeLabel(record.type)}</div>
                            <div><span className="font-medium">{p('label_country')}:</span> {record.country || p('default_country')}</div>
                            <div className="pt-2 border-t border-white/20">
                              <span className="font-medium text-white">{p('full_address')}:</span>
                              <div className="text-white/90 text-xs mt-1">{record.address || p('not_provided_f')}, {record.city || ''}</div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Capacité & Stock */}
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_capacity_stock')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div className="flex items-center gap-2">
                              <Gauge size={16} className="text-blue-400" />
                              <span className="font-medium">{p('col_capacity')}:</span> <span className="text-white">{record.capacity || 0} m²</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Package size={16} className="text-yellow-400" />
                              <span className="font-medium">{p('label_stock')}:</span> <span className="text-white">{record.current_stock || 0}</span>
                            </div>
                            <div className="pt-2">
                              <span className="font-medium">{p('fill_rate_label')}:</span>
                              <div className="w-full bg-white/10 h-2 rounded-full overflow-hidden mt-1">
                                <div 
                                  className="h-2 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all" 
                                  style={{ width: `${record.capacity > 0 ? ((record.current_stock / record.capacity) * 100).toFixed(0) : 0}%` }}
                                ></div>
                              </div>
                              <span className="text-white/90 text-xs mt-1 block">
                                {record.capacity > 0 ? ((record.current_stock / record.capacity) * 100).toFixed(0) : 0}%
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Localisation */}
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_location')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            {record.latitude && record.longitude ? (
                              <div className="p-2 bg-blue-500/10 rounded border border-blue-500/30">
                                <span className="font-medium text-blue-300 text-xs">{p('gps')}:</span>
                                <p className="text-blue-200 text-xs mt-1">{record.latitude?.toFixed(4)}, {record.longitude?.toFixed(4)}</p>
                              </div>
                            ) : (
                              <div><span className="font-medium">{p('gps')}:</span> {p('not_provided')}</div>
                            )}
                            {record.postal_code && (
                              <div><span className="font-medium">{p('col_postal_code')}:</span> {record.postal_code}</div>
                            )}
                            <div><span className="font-medium">{p('col_city')}:</span> {record.city || '-'}</div>
                            {!record.latitude && !record.longitude && !record.postal_code && (
                              <p className="text-white/40 text-xs italic">{p('no_location_data')}</p>
                            )}
                          </div>
                        </div>
                        
                        {/* Contacts & Responsables */}
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_contacts')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            {record.contact_person ? (
                              <div><span className="font-medium">{p('col_contact')}:</span> {record.contact_person}</div>
                            ) : (
                              <div><span className="font-medium">{p('col_contact')}:</span> {p('not_provided')}</div>
                            )}
                            {record.phone ? (
                              <div><span className="font-medium">{p('col_phone')}:</span> {record.phone}</div>
                            ) : (
                              <div><span className="font-medium">{p('col_phone')}:</span> {p('not_provided')}</div>
                            )}
                            {record.email && (
                              <div><span className="font-medium">{p('col_email')}:</span> {record.email}</div>
                            )}
                            {record.manager && (
                              <div className="pt-2 border-t border-white/20">
                                <span className="font-medium">{p('col_manager')}:</span> {record.manager}
                              </div>
                            )}
                            {record.notes && (
                              <div className="mt-2 p-2 bg-white/5 rounded">
                                <span className="font-medium text-white/80 text-xs">{p('label_notes')}:</span>
                                <p className="text-white/60 text-xs mt-1">{record.notes}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex gap-1 ml-4 shrink-0">
                      <QawaemErpRowActions
                        compact
                        size={14}
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                      viewTitle={t('common.view')}
                      editTitle={t('common.edit')}
                      deleteTitle={t('common.delete')}
                      />
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <Warehouse size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty')}</p>
              </div>
            )}
          </div>
        )}
    </InventaireListPageShell>

      {/* View Modal - Shows 100% of data - WIDE LAYOUT */}
      {showViewModal && viewingRecord && ReactDOM.createPortal(
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
            onClick={() => setShowViewModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
              style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">{p('view_title')}</h2>
                <button onClick={() => setShowViewModal(false)} className="text-white/70 hover:text-white transition-colors">
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-4">
                {/* Informations Générales */}
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Warehouse size={18} className="text-emerald-400" />
                    {p('section_general')}
                  </h3>
                  <div className={INVENTAIRE_FORM_GRID}>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_name')}</p>
                      <p className="text-white font-medium text-lg">{viewingRecord.name}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_code')}</p>
                      <p className="text-white font-medium font-mono text-sm">{viewingRecord.code || '-'}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('type_label')}</p>
                      <p className="text-white font-medium">{getTypeLabel(viewingRecord.type)}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_status')}</p>
                      <div className="flex"><QawaemClickableStatusBadge status={viewingRecord.status} recordId={viewingRecord.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status') || t('common.change_status')} /></div>
                    </div>
                    <div className="md:col-span-4">
                      <p className="text-white/60 text-sm mb-1">{p('label_address')}</p>
                      <p className="text-white font-medium">{viewingRecord.address || p('not_provided_f')}, {viewingRecord.city || ''} {viewingRecord.postal_code || ''}, {viewingRecord.country || p('default_country')}</p>
                    </div>
                  </div>
                </div>

                {/* Capacité & Stock */}
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Gauge size={18} className="text-blue-400" />
                    {p('section_capacity_stock')}
                  </h3>
                  <div className={INVENTAIRE_FORM_GRID}>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_total_capacity')}</p>
                      <p className="text-white font-medium text-lg">{viewingRecord.capacity || 0} m²</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_current_stock')}</p>
                      <p className="text-white font-medium">{viewingRecord.current_stock || 0}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_fill_rate')}</p>
                      <p className="text-white font-medium">
                        {viewingRecord.capacity > 0 ? ((viewingRecord.current_stock / viewingRecord.capacity) * 100).toFixed(0) : 0}%
                      </p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_available_capacity')}</p>
                      <p className="text-white font-medium">{(viewingRecord.capacity - viewingRecord.current_stock) || 0} m²</p>
                    </div>
                  </div>
                </div>

                {/* Grid layout for Localisation and Contacts */}
                <div className={INVENTAIRE_FORM_GRID}>
                  {/* Localisation */}
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <MapPin size={18} className="text-yellow-400" />
                      {p('section_location')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('col_city')}</p>
                        <p className="text-white font-medium">{viewingRecord.city || '-'}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('col_postal_code')}</p>
                        <p className="text-white font-medium">{viewingRecord.postal_code || '-'}</p>
                      </div>
                      {viewingRecord.latitude && viewingRecord.longitude && (
                        <div>
                          <p className="text-white/60 text-sm mb-1">{p('label_gps_coords')}</p>
                          <p className="text-white font-medium font-mono text-xs">{viewingRecord.latitude?.toFixed(4)}, {viewingRecord.longitude?.toFixed(4)}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Contacts & Responsables */}
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <Shield size={18} className="text-red-400" />
                      {p('section_contacts')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_contact_person')}</p>
                        <p className="text-white font-medium">{viewingRecord.contact_person || p('not_provided')}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('col_phone')}</p>
                        <p className="text-white font-medium">{viewingRecord.phone || p('not_provided')}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('col_email')}</p>
                        <p className="text-white font-medium text-xs">{viewingRecord.email || p('not_provided')}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('col_manager')}</p>
                        <p className="text-white font-medium">{viewingRecord.manager || '-'}</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Notes */}
                {viewingRecord.notes && (
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-2">{p('label_notes')}</h3>
                    <p className="text-white/70 text-sm">{viewingRecord.notes}</p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}

      {/* Edit/Create Modal */}
      {showModal && (
        <InventaireModalPortal show={showModal} onClose={resetForm} maxWidth="1200px">
            {/* Fixed Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/10 shrink-0">
              <h2 className="text-2xl font-bold text-white">{editingRecord ? p('edit_warehouse') : p('new_warehouse')}</h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="warehouse-form" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30">
                  <CheckCircle size={18} />
                  {editingRecord ? p('update') : p('create')}
                </button>
                <button onClick={resetForm} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
              </div>
            </div>

            <FacturationFormModalTabs
              tabs={warehouseFormTabs}
              activeTab={warehouseFormTab}
              onTabChange={setWarehouseFormTab}
              isLight={isDashboardLightMode}
              ariaLabel={editingRecord ? p('edit_warehouse') : p('new_warehouse')}
            />

            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="warehouse-form" onSubmit={handleSubmit}>
              {warehouseFormTab === 'warehouseInfo' && (
              <div className={INVENTAIRE_FORM_GRID}>
                        <div>
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_code', { defaultValue: 'الرمز' })}</label>
                          <input
                            type="text"
                            required
                            value={formData.code || ''}
                            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                            className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-mono"
                            placeholder={p('placeholder_code', { defaultValue: 'WH-001' })}
                            dir="ltr"
                          />
                        </div>
                        <div>
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('label_name')} required /></label>
                          <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_warehouse_name')} />
                        </div>
                        <div>
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('type_label')} required /></label>
                          <div className="relative" ref={typeDropdownRef}>
                            <button
                              type="button"
                              data-warehouse-type-dropdown-button="true"
                              onClick={() => setShowTypeDropdown(!showTypeDropdown)}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all flex items-center justify-between"
                            >
                              <span>
                                {getTypeLabel(formData.type)}
                              </span>
                              <ChevronDown size={16} className={`transition-transform ${showTypeDropdown ? 'rotate-180' : ''}`} />
                            </button>
                            {showTypeDropdown && ReactDOM.createPortal(
                              <div 
                                data-warehouse-type-dropdown="true"
                                className="border border-blue-400/60 rounded-2xl shadow-2xl overflow-hidden"
                                style={{ 
                                  backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', 
                                  position: 'fixed', 
                                  ...(typeDropdownRef.current && (() => {
                                    const rect = typeDropdownRef.current.getBoundingClientRect();
                                    const spaceBelow = window.innerHeight - rect.bottom;
                                    const spaceAbove = rect.top;
                                    const dropdownHeight = 180;
                                    
                                    if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
                                      return { bottom: window.innerHeight - rect.top + 8, left: rect.left, width: rect.width };
                                    } else {
                                      return { top: rect.bottom + 8, left: rect.left, width: rect.width };
                                    }
                                  })()),
                                  zIndex: 99999, 
                                  boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)' 
                                }}
                              >
                                <button type="button" onClick={() => { setFormData({ ...formData, type: 'main' }); setShowTypeDropdown(false); }} className="w-full flex items-center px-4 py-2.5 hover:bg-blue-500/20 transition-all text-left text-white text-sm font-medium">{p('type_main')}</button>
                                <button type="button" onClick={() => { setFormData({ ...formData, type: 'secondary' }); setShowTypeDropdown(false); }} className="w-full flex items-center px-4 py-2.5 hover:bg-blue-500/20 transition-all text-left text-white text-sm font-medium">{p('type_secondary')}</button>
                                <button type="button" onClick={() => { setFormData({ ...formData, type: 'distribution' }); setShowTypeDropdown(false); }} className="w-full flex items-center px-4 py-2.5 hover:bg-blue-500/20 transition-all text-left text-white text-sm font-medium">{p('type_distribution')}</button>
                                <button type="button" onClick={() => { setFormData({ ...formData, type: 'transit' }); setShowTypeDropdown(false); }} className="w-full flex items-center px-4 py-2.5 hover:bg-blue-500/20 transition-all text-left text-white text-sm font-medium">{p('type_transit')}</button>
                              </div>,
                              document.body
                            )}
                          </div>
                        </div>
                        <div>
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('col_status')} required /></label>
                          <div className="relative" ref={statusDropdownRef}>
                            <button
                              type="button"
                              data-warehouse-status-dropdown-button="true"
                              onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all flex items-center justify-between"
                            >
                              <span>
                                {getStatusLabel(formData.status)}
                              </span>
                              <ChevronDown size={16} className={`transition-transform ${showStatusDropdown ? 'rotate-180' : ''}`} />
                            </button>
                            {showStatusDropdown && ReactDOM.createPortal(
                              <div 
                                data-warehouse-status-dropdown="true"
                                className="border border-blue-400/60 rounded-2xl shadow-2xl overflow-hidden"
                                style={{ 
                                  backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', 
                                  position: 'fixed', 
                                  ...(statusDropdownRef.current && (() => {
                                    const rect = statusDropdownRef.current.getBoundingClientRect();
                                    const spaceBelow = window.innerHeight - rect.bottom;
                                    const spaceAbove = rect.top;
                                    const dropdownHeight = 180;
                                    
                                    if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
                                      return { bottom: window.innerHeight - rect.top + 8, left: rect.left, width: rect.width };
                                    } else {
                                      return { top: rect.bottom + 8, left: rect.left, width: rect.width };
                                    }
                                  })()),
                                  zIndex: 99999, 
                                  boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)' 
                                }}
                              >
                                <button type="button" onClick={() => { setFormData({ ...formData, status: 'active' }); setShowStatusDropdown(false); }} className="w-full flex items-center px-4 py-2.5 hover:bg-blue-500/20 transition-all text-left text-white text-sm font-medium">{p('status_active')}</button>
                                <button type="button" onClick={() => { setFormData({ ...formData, status: 'inactive' }); setShowStatusDropdown(false); }} className="w-full flex items-center px-4 py-2.5 hover:bg-blue-500/20 transition-all text-left text-white text-sm font-medium">{p('status_inactive')}</button>
                                <button type="button" onClick={() => { setFormData({ ...formData, status: 'maintenance' }); setShowStatusDropdown(false); }} className="w-full flex items-center px-4 py-2.5 hover:bg-blue-500/20 transition-all text-left text-white text-sm font-medium">{p('status_maintenance')}</button>
                                <button type="button" onClick={() => { setFormData({ ...formData, status: 'construction' }); setShowStatusDropdown(false); }} className="w-full flex items-center px-4 py-2.5 hover:bg-blue-500/20 transition-all text-left text-white text-sm font-medium">{p('status_construction')}</button>
                              </div>,
                              document.body
                            )}
                          </div>
                        </div>
                        <div>
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_current_stock')}</label>
                          <CustomNumberInput
                            value={formData.current_stock}
                            onChange={(val) => setFormData({ ...formData, current_stock: val === '' ? '' : val })}
                            step={1}
                            min={0}
                            placeholder="0"
                          />
                        </div>
                        <div className="md:col-span-3">
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_address')}</label>
                          <input type="text" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_address')} />
                        </div>
                        <div>
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_city')}</label>
                          <input type="text" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_city')} />
                        </div>
                        <div>
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_postal_code')}</label>
                          <input type="text" value={formData.postal_code} onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder="16000" />
                        </div>
                        <div>
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_country')}</label>
                          <CustomSelect
                            value={formData.country}
                            onChange={(value) => setFormData({ ...formData, country: value })}
                            options={countryOptions}
                            placeholder={p('placeholder_country')}
                            searchable
                            creatable
                          />
                        </div>
                        <div>
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_latitude')}</label>
                          <CustomNumberInput
                            value={formData.latitude}
                            onChange={(val) => setFormData({ ...formData, latitude: val === '' ? '' : val })}
                            step={0.0001}
                            decimals={4}
                            placeholder="36.7538"
                          />
                        </div>
                        <div>
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_longitude')}</label>
                          <CustomNumberInput
                            value={formData.longitude}
                            onChange={(val) => setFormData({ ...formData, longitude: val === '' ? '' : val })}
                            step={0.0001}
                            decimals={4}
                            placeholder="3.0588"
                          />
                        </div>
                        <div>
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_total_capacity')} (m²)</label>
                          <CustomNumberInput
                            value={formData.capacity}
                            onChange={(val) => setFormData({ ...formData, capacity: val === '' ? '' : val })}
                            step={100}
                            min={0}
                            decimals={1}
                            placeholder="10000"
                          />
                        </div>
              </div>
              )}

              {warehouseFormTab === 'contactSecurity' && (
              <div className={INVENTAIRE_FORM_GRID}>
                        <div>
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_contact_person')}</label>
                          <input type="text" value={formData.contact_person} onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_contact_name')} />
                        </div>
                        <div>
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_phone')}</label>
                          <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder="+213 XXX XXX XXX" />
                        </div>
                        <div>
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_email')}</label>
                          <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('ph_email')} />
                        </div>
                        <div>
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_manager')}</label>
                          <input type="text" value={formData.manager} onChange={(e) => setFormData({ ...formData, manager: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_manager_name')} />
                        </div>
                        <div className="md:col-span-3">
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_notes')}</label>
                          <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_notes')} rows={3}></textarea>
                        </div>
              </div>
              )}
              </form>
            </div>
        </InventaireModalPortal>
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </>
  );
};

export default WarehousesPage;


